// src/components/ui/Select.jsx
// Использование:
//   <Select label="Статус" value={val} onChange={e => setVal(e.target.value)}>
//     <option value="">Все</option>
//     <option value="DRAFT">Черновик</option>
//   </Select>
//
//   <Select label="Организация *" required error="Выберите организацию">
//     <option value="">Выберите...</option>
//   </Select>
//
//   <Select variant="ghost" value={val} onChange={...}>
//     <option>...</option>
//   </Select>

import React from 'react';
import { ChevronDown, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const variants = {
  default: 'bg-gray-50 border-gray-200 text-gray-900 focus:border-red-600/60',       // стандартный — как Input
  ghost:   'bg-white border-gray-200 text-gray-900 hover:border-gray-300 focus:border-red-600/60', // добавлен: для модальных окон с белым фоном
};

const sizes = {
  sm: 'py-2 pl-3 pr-9 text-xs',
  md: 'py-3 pl-4 pr-10 text-sm',
  lg: 'py-4 pl-4 pr-10 text-sm',
};

export const Select = ({
  label,
  value,
  onChange,
  error,
  required = false,
  optional = false,
  variant = 'default',
  size = 'md',
  disabled = false,
  className = '',
  children,
}) => {
  const { t } = useTranslation();
  return (
    <div className={className}>
      {label && (
        <label className="text-label font-black uppercase tracking-widest text-gray-400 mb-1.5 block">
          {label}
          {required && <span className="text-red-600 ml-1">*</span>}
          {optional && <span className="text-gray-500 ml-1">{t('ui_Select.optional')}</span>}
        </label>
      )}
      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={`
            w-full appearance-none rounded-sm border outline-none
            transition-all
            disabled:opacity-50 disabled:cursor-not-allowed
            ${variants[variant] ?? variants.default}
            ${sizes[size] ?? sizes.md}
            ${error ? 'border-red-500/50' : ''}
          `}
        >
          {children}
        </select>
        {/* Кастомная стрелка */}
        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
          <ChevronDown size={14} />
        </div>
      </div>
      {error && (
        <p className="text-red-600 text-[10px] mt-1 flex items-center gap-1">
          <AlertCircle size={10} /> {error}
        </p>
      )}
    </div>
  );
};
